/**
 * Account Payment & Invoices Page - Admin
 * Integrated with Stripe for real payment processing
 */

import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import api from '../../api';
import {
  CreditCard, FileText, Send, Key, Search, User, Eye, EyeOff,
  DollarSign, Calendar, CheckCircle, AlertTriangle, RefreshCw,
  Receipt, Download, ExternalLink, Clock, Mail, Zap, Building,
  Users, Shield, Star
} from 'lucide-react';

const AccountBillingPage = () => {
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    loadData();
    checkReturnFromStripe();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [plansRes, subRes, invoicesRes] = await Promise.all([
        api.get('/api/payments/plans'),
        api.get('/api/payments/subscription'),
        api.get('/api/payments/invoices')
      ]);
      
      setPlans(plansRes.data?.plans || []);
      setSubscription(subRes.data?.subscription || null);
      setInvoices(invoicesRes.data?.invoices || []);
    } catch (error) {
      console.error('Error loading billing data:', error);
      // Set default plans
      setPlans([
        { id: 'starter', name: 'Starter', amount: 299.00, currency: 'usd', features: ['Up to 50 patients', '2 users', 'Basic scheduling', 'Email support'] },
        { id: 'professional', name: 'Professional', amount: 599.00, currency: 'usd', features: ['Up to 200 patients', '10 users', 'Advanced EVV', 'Priority support', 'API access'] },
        { id: 'enterprise', name: 'Enterprise', amount: 1499.00, currency: 'usd', features: ['Unlimited patients', 'Unlimited users', 'Multi-office', '24/7 support', 'Custom integrations'] }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const checkReturnFromStripe = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please refresh the page.');
      return;
    }

    try {
      const response = await api.get(`/api/payments/checkout/status/${sessionId}`);
      
      if (response.data?.payment_status === 'paid') {
        toast.success('Payment successful! Your subscription is now active.');
        loadData();
        return;
      } else if (response.data?.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const handleSubscribe = async (planId) => {
    setProcessingPayment(true);
    setSelectedPlan(planId);
    
    try {
      const response = await api.post('/api/payments/checkout', {
        plan_id: planId,
        origin_url: window.location.origin
      });
      
      if (response.data?.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setProcessingPayment(false);
      setSelectedPlan(null);
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'starter': return <Building className="w-6 h-6" />;
      case 'professional': return <Users className="w-6 h-6" />;
      case 'enterprise': return <Shield className="w-6 h-6" />;
      default: return <Zap className="w-6 h-6" />;
    }
  };

  const getPlanColor = (planId) => {
    switch (planId) {
      case 'starter': return 'blue';
      case 'professional': return 'purple';
      case 'enterprise': return 'orange';
      default: return 'gray';
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="account-billing-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-blue-600" />
            Account Payment and Invoices
          </h1>
          <p className="text-gray-600 mt-1">Manage your subscription, payments, and invoices</p>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto text-blue-600" />
            <p className="mt-2 text-gray-600">Loading billing information...</p>
          </div>
        ) : (
          <>
            {/* Current Subscription Status */}
            {subscription && subscription.status === 'active' && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                  <div>
                    <h2 className="text-lg font-semibold text-green-800">Active Subscription</h2>
                    <p className="text-green-600">
                      You're on the <strong className="capitalize">{subscription.plan}</strong> plan
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Subscription Plans */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Subscription Plans</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {plans.map((plan) => {
                  const isCurrentPlan = subscription?.plan === plan.id;
                  const color = getPlanColor(plan.id);
                  
                  return (
                    <div 
                      key={plan.id} 
                      className={`relative bg-white rounded-xl border-2 p-6 transition-all ${
                        plan.id === 'professional' ? 'border-purple-500 shadow-lg scale-[1.02]' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {plan.id === 'professional' && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-500 text-white px-4 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                          <Star className="w-4 h-4" />Most Popular
                        </div>
                      )}
                      
                      <div className={`p-3 bg-${color}-100 rounded-lg w-fit mb-4`}>
                        {getPlanIcon(plan.id)}
                      </div>
                      
                      <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                      <div className="mt-2 flex items-baseline gap-1">
                        <span className="text-3xl font-bold text-gray-900">${plan.amount}</span>
                        <span className="text-gray-500">/month</span>
                      </div>
                      
                      <ul className="mt-6 space-y-3">
                        {plan.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-gray-600">
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      
                      <Button
                        onClick={() => handleSubscribe(plan.id)}
                        disabled={processingPayment || isCurrentPlan}
                        className={`w-full mt-6 ${
                          isCurrentPlan 
                            ? 'bg-gray-100 text-gray-500 cursor-not-allowed' 
                            : plan.id === 'professional'
                              ? 'bg-purple-600 hover:bg-purple-700'
                              : 'bg-blue-600 hover:bg-blue-700'
                        }`}
                        data-testid={`subscribe-${plan.id}-btn`}
                      >
                        {processingPayment && selectedPlan === plan.id ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : isCurrentPlan ? (
                          'Current Plan'
                        ) : (
                          <>
                            <CreditCard className="w-4 h-4 mr-2" />
                            Subscribe Now
                          </>
                        )}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Billing Summary */}
            {subscription?.status === 'active' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg border p-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-green-100 rounded-lg">
                      <DollarSign className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Current Balance</p>
                      <p className="text-2xl font-bold text-gray-900">$0.00</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg border p-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-blue-100 rounded-lg">
                      <Calendar className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Next Billing Date</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {subscription.next_billing || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg border p-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-purple-100 rounded-lg">
                      <Receipt className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Total Invoices</p>
                      <p className="text-2xl font-bold text-gray-900">{invoices.length}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="bg-white rounded-lg border p-6">
              <h2 className="font-semibold text-gray-900 mb-4">Quick Actions</h2>
              <div className="flex flex-wrap gap-3">
                <Button variant="outline">
                  <FileText className="w-4 h-4 mr-2" />
                  View All Invoices
                </Button>
                <Button variant="outline">
                  <Download className="w-4 h-4 mr-2" />
                  Download Statement
                </Button>
                <Button variant="outline">
                  <Mail className="w-4 h-4 mr-2" />
                  Update Billing Email
                </Button>
              </div>
            </div>

            {/* Payment History */}
            {invoices.length > 0 && (
              <div className="bg-white rounded-lg border overflow-hidden">
                <div className="px-6 py-4 border-b">
                  <h2 className="font-semibold text-gray-900">Payment History</h2>
                </div>
                <table className="w-full" data-testid="invoices-table">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {invoices.map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium text-blue-600">{invoice.id}</td>
                        <td className="px-6 py-4 text-gray-600">
                          {invoice.date ? new Date(invoice.date).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-gray-600 capitalize">{invoice.plan}</td>
                        <td className="px-6 py-4 font-medium">${invoice.amount?.toFixed(2)}</td>
                        <td className="px-6 py-4">
                          <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
                            {invoice.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm">
                              <FileText className="w-4 h-4" />
                            </Button>
                            <Button variant="outline" size="sm">
                              <Download className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Payment Methods */}
            <div className="bg-white rounded-lg border p-6">
              <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-purple-600" />
                Accepted Payment Methods
              </h2>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg">
                  <CreditCard className="w-5 h-5 text-blue-600" />
                  <span>Visa</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg">
                  <CreditCard className="w-5 h-5 text-red-600" />
                  <span>Mastercard</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg">
                  <CreditCard className="w-5 h-5 text-blue-800" />
                  <span>American Express</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  <span>ACH Transfer</span>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-4">
                Payments are securely processed through Stripe. Your card information is never stored on our servers.
              </p>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AccountBillingPage;
